export const VIEW_MODES = {
	LIST: 'list',
	FLAT: 'flat',
	BOARD: 'board',
	CALENDAR: 'calendar',
};

export const STATUS_GROUPS = [
	{
		key: 'not_started',
		label: 'Not Started',
		colorScheme: 'gray',
		bg: 'gray.50',
		icon: 'empty',
	},
	{
		key: 'pending',
		label: 'Pending',
		colorScheme: 'blue',
		bg: 'blue.50',
		icon: 'circle',
	},
	{
		key: 'in_progress',
		label: 'In Progress',
		colorScheme: 'yellow',
		bg: 'yellow.50',
		icon: 'half',
	},
	{
		key: 'review',
		label: 'Ready to Review',
		colorScheme: 'purple',
		bg: 'purple.50',
		icon: 'review',
	},
	{
		key: 'completed',
		label: 'Reviewed & Completed',
		colorScheme: 'green',
		bg: 'green.50',
		icon: 'check',
	},
];

const STATUS_GROUP_PRESENTATION = {
	not_started: {
		headerBg: { light: 'gray.50', dark: 'whiteAlpha.50' },
		bodyBg: { light: 'white', dark: 'gray.800' },
		border: { light: 'gray.200', dark: 'gray.600' },
		accent: { light: 'gray.400', dark: 'gray.500' },
		badgeBg: { light: 'gray.100', dark: 'whiteAlpha.200' },
		badgeColor: { light: 'gray.700', dark: 'gray.100' },
		iconColor: { light: 'gray.500', dark: 'gray.300' },
	},
	pending: {
		headerBg: { light: 'blue.50', dark: 'blue.900' },
		bodyBg: { light: 'white', dark: 'gray.800' },
		border: { light: 'blue.200', dark: 'blue.700' },
		accent: { light: 'blue.400', dark: 'blue.300' },
		badgeBg: { light: 'blue.100', dark: 'blue.800' },
		badgeColor: { light: 'blue.800', dark: 'blue.100' },
		iconColor: { light: 'blue.500', dark: 'blue.200' },
	},
	in_progress: {
		headerBg: { light: 'yellow.50', dark: 'yellow.900' },
		bodyBg: { light: 'white', dark: 'gray.800' },
		border: { light: 'yellow.200', dark: 'yellow.700' },
		accent: { light: 'yellow.500', dark: 'yellow.300' },
		badgeBg: { light: 'yellow.100', dark: 'yellow.800' },
		badgeColor: { light: 'yellow.900', dark: 'yellow.100' },
		iconColor: { light: 'yellow.600', dark: 'yellow.200' },
	},
	review: {
		headerBg: { light: 'purple.50', dark: 'purple.900' },
		bodyBg: { light: 'white', dark: 'gray.800' },
		border: { light: 'purple.200', dark: 'purple.700' },
		accent: { light: 'purple.500', dark: 'purple.300' },
		badgeBg: { light: 'purple.100', dark: 'purple.800' },
		badgeColor: { light: 'purple.800', dark: 'purple.100' },
		iconColor: { light: 'purple.600', dark: 'purple.200' },
	},
	completed: {
		headerBg: { light: 'green.50', dark: 'green.900' },
		bodyBg: { light: 'white', dark: 'gray.800' },
		border: { light: 'green.200', dark: 'green.700' },
		accent: { light: 'green.500', dark: 'green.300' },
		badgeBg: { light: 'green.100', dark: 'green.800' },
		badgeColor: { light: 'green.800', dark: 'green.100' },
		iconColor: { light: 'green.600', dark: 'green.200' },
	},
};

const pickModeValue = (pair, isDark) => (isDark ? pair.dark : pair.light);

export const getStatusGroupPresentation = (groupKey, colorMode = 'light') => {
	const meta = getStatusGroupMeta(groupKey);
	const palette =
		STATUS_GROUP_PRESENTATION[groupKey] || STATUS_GROUP_PRESENTATION.not_started;
	const isDark = colorMode === 'dark';

	return {
		...meta,
		headerBg: pickModeValue(palette.headerBg, isDark),
		bodyBg: pickModeValue(palette.bodyBg, isDark),
		borderColor: pickModeValue(palette.border, isDark),
		accentColor: pickModeValue(palette.accent, isDark),
		badgeBg: pickModeValue(palette.badgeBg, isDark),
		badgeColor: pickModeValue(palette.badgeColor, isDark),
		iconColor: pickModeValue(palette.iconColor, isDark),
	};
};

export const WORK_ITEM_STATUS_OPTIONS = [
	'not started',
	'pending',
	'in progress',
	'ready to review',
	'reviewed & completed',
];

export const TODO_STATUS_OPTIONS = WORK_ITEM_STATUS_OPTIONS;
export const TASK_STATUS_OPTIONS = WORK_ITEM_STATUS_OPTIONS;

const LEGACY_STATUS_MAP = {
	'not started': 'not started',
	not_started: 'not started',
	'Not Started': 'not started',
	pending: 'pending',
	Pending: 'pending',
	'in progress': 'in progress',
	in_progress: 'in progress',
	'In Progress': 'in progress',
	'ready to review': 'ready to review',
	'In Review': 'ready to review',
	in_review: 'ready to review',
	completed: 'reviewed & completed',
	Completed: 'reviewed & completed',
	'reviewed & completed': 'reviewed & completed',
	'On Hold': 'pending',
	Cancelled: 'pending',
};

export const WORK_ITEM_STATUS_LABELS = {
	'not started': 'Not Started',
	pending: 'Pending',
	'in progress': 'In Progress',
	'ready to review': 'Ready to Review',
	completed: 'Reviewed & Completed',
	'reviewed & completed': 'Reviewed & Completed',
};

export const TODO_STATUS_LABELS = WORK_ITEM_STATUS_LABELS;

export const normalizeWorkItemStatus = (status) => {
	if (!status) return 'not started';
	if (LEGACY_STATUS_MAP[status]) return LEGACY_STATUS_MAP[status];
	const lower = String(status).toLowerCase();
	return LEGACY_STATUS_MAP[lower] || lower;
};

export const getWorkItemStatusLabel = (status) =>
	WORK_ITEM_STATUS_LABELS[normalizeWorkItemStatus(status)] ||
	WORK_ITEM_STATUS_LABELS[String(status).toLowerCase()] ||
	status;

export const getTodoStatusLabel = getWorkItemStatusLabel;

export const isReadyToReviewStatus = (status) =>
	normalizeWorkItemStatus(status) === 'ready to review';

export const isTodoCompletedStatus = (status) =>
	normalizeWorkItemStatus(status) === 'reviewed & completed';

export const normalizeTodoStatus = (status) => normalizeWorkItemStatus(status);

export const TODO_STATUS_CHIP_OPTIONS = [
	{ label: 'All', value: 'all' },
	{ label: 'Not Started', value: 'not started' },
	{ label: 'Pending', value: 'pending' },
	{ label: 'In Progress', value: 'in progress' },
	{ label: 'Ready to Review', value: 'ready to review' },
	{ label: 'Reviewed & Completed', value: 'reviewed & completed' },
];

export const WORK_ITEM_STATUS_FORM_OPTIONS = WORK_ITEM_STATUS_OPTIONS.map((value) => ({
	label: WORK_ITEM_STATUS_LABELS[value],
	value,
}));

export const WORK_ITEM_STATUS_MENU_OPTIONS = WORK_ITEM_STATUS_FORM_OPTIONS;

export const WORK_ITEM_STATUS_COLOR_SCHEME = {
	'not started': 'gray',
	pending: 'blue',
	'in progress': 'yellow',
	'ready to review': 'purple',
	'reviewed & completed': 'green',
};

export const getWorkItemStatusColor = (status) =>
	WORK_ITEM_STATUS_COLOR_SCHEME[normalizeWorkItemStatus(status)] || 'gray';

export const WORK_ITEM_STATUS_CHART_COLORS = {
	'not started': '#A0AEC0',
	pending: '#CBD5E0',
	'in progress': '#48BB78',
	'ready to review': '#4299E1',
	'reviewed & completed': '#9F7AEA',
};

export const normalizeMilestoneStatus = (status) => {
	const milestoneMap = { Pending: 'pending', Delayed: 'pending' };
	return normalizeWorkItemStatus(milestoneMap[status] || status);
};

export const countItemsByWorkItemStatus = (
	items = [],
	getStatus = (item) => item.status,
) => {
	const counts = Object.fromEntries(WORK_ITEM_STATUS_OPTIONS.map((s) => [s, 0]));
	items.forEach((item) => {
		const normalized = normalizeWorkItemStatus(getStatus(item));
		if (counts[normalized] !== undefined) counts[normalized]++;
	});
	return counts;
};

export const getStatusGroup = (status = '') => {
	const normalized = normalizeWorkItemStatus(status);
	if (isTodoCompletedStatus(normalized)) return 'completed';
	if (normalized === 'in progress') return 'in_progress';
	if (normalized === 'ready to review') return 'review';
	if (normalized === 'not started') return 'not_started';
	if (normalized === 'pending') return 'pending';
	return 'not_started';
};

export const getStatusGroupMeta = (groupKey) =>
	STATUS_GROUPS.find((group) => group.key === groupKey) || STATUS_GROUPS[0];

export const getStatusForGroupKey = (groupKey) => {
	const map = {
		not_started: 'not started',
		pending: 'pending',
		in_progress: 'in progress',
		review: 'ready to review',
		completed: 'reviewed & completed',
	};
	return map[groupKey] || 'not started';
};

export const getBoardDraggableId = (item) => `${item.itemType}:${item.id}`;

export const findBoardItemByDraggableId = (items, draggableId) =>
	items.find((item) => getBoardDraggableId(item) === draggableId);

export const getPriorityColor = (priority) => {
	const normalized = String(priority || '').toLowerCase();
	const map = {
		critical: 'red',
		urgent: 'red',
		high: 'orange',
		medium: 'yellow',
		low: 'green',
	};
	return map[normalized] || 'gray';
};

export const LIST_SORT_FIELDS = {
	NAME: 'name',
	ASSIGNEE: 'assignee',
	DUE_DATE: 'dueDate',
	PRIORITY: 'priority',
	STATUS: 'status',
};

const PRIORITY_RANK = {
	critical: 0,
	urgent: 1,
	high: 2,
	medium: 3,
	low: 4,
};

export const getItemPriorityRank = (priority) => {
	if (!priority) return 99;
	return PRIORITY_RANK[String(priority).toLowerCase()] ?? 50;
};

const getItemDueTime = (item) => {
	if (!item?.dueDate) return null;
	return new Date(item.dueDate).getTime();
};

const getPrimaryAssigneeName = (item) => item.assignees?.[0]?.name?.trim() || '';

export const sortWorkspaceItems = (items, field, direction = 'asc') => {
	const dir = direction === 'desc' ? -1 : 1;

	return [...items].sort((a, b) => {
		let cmp = 0;

		switch (field) {
			case LIST_SORT_FIELDS.NAME:
				cmp = (a.title || '').localeCompare(b.title || '', undefined, {
					sensitivity: 'base',
				});
				break;
			case LIST_SORT_FIELDS.ASSIGNEE: {
				const aName = getPrimaryAssigneeName(a);
				const bName = getPrimaryAssigneeName(b);
				if (!aName && bName) cmp = 1;
				else if (aName && !bName) cmp = -1;
				else cmp = aName.localeCompare(bName, undefined, { sensitivity: 'base' });
				break;
			}
			case LIST_SORT_FIELDS.DUE_DATE: {
				const aDue = getItemDueTime(a);
				const bDue = getItemDueTime(b);
				if (aDue === null && bDue === null) cmp = 0;
				else if (aDue === null) cmp = 1;
				else if (bDue === null) cmp = -1;
				else cmp = aDue - bDue;
				break;
			}
			case LIST_SORT_FIELDS.PRIORITY:
				cmp = getItemPriorityRank(a.priority) - getItemPriorityRank(b.priority);
				break;
			case LIST_SORT_FIELDS.STATUS:
				cmp = String(a.status || '').localeCompare(String(b.status || ''), undefined, {
					sensitivity: 'base',
				});
				break;
			default:
				cmp = 0;
		}

		return cmp * dir;
	});
};

export const formatDueDate = (date) => {
	if (!date) return null;
	const d = new Date(date);
	return d.toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
	});
};

export const isOverdue = (item) => {
	if (!item?.dueDate) return false;
	if (isTodoCompletedStatus(item.status)) return false;
	return new Date(item.dueDate) < new Date();
};

export const getAssigneeName = (assignee) => {
	if (!assignee) return '';
	if (typeof assignee === 'string') return assignee;
	const name = `${assignee.firstName || ''} ${assignee.lastName || ''}`.trim();
	return name || assignee.email || assignee.name || 'User';
};

export const getAssigneeInitials = (assignee) => {
	const name = getAssigneeName(assignee);
	return name
		.split(' ')
		.filter(Boolean)
		.slice(0, 2)
		.map((part) => part[0]?.toUpperCase() || '')
		.join('');
};

const mapReviewer = (reviewer) => {
	if (!reviewer) return null;
	const id = String(reviewer._id || reviewer);
	return {
		id,
		name: getAssigneeName(reviewer),
		avatar: reviewer.avatar || reviewer.profileImage || null,
	};
};

export const getTodoCreatorId = (todo) => {
	const creator = (todo?.users || [])[0];
	if (!creator) return null;
	return String(creator._id || creator);
};

/** Tasks/todos may store assignees as an array or a single id/user object. */
export const normalizeAssignees = (assignedTo) => {
	if (!assignedTo) return [];
	if (Array.isArray(assignedTo)) return assignedTo;
	return [assignedTo];
};

export const hasNonCreatorAssignees = (todo, assignees = todo?.assignedTo) =>
	normalizeAssignees(assignees).some((user) => {
		const id = String(user._id || user);
		const creatorId = getTodoCreatorId(todo);
		return creatorId ? id !== creatorId : true;
	});

/** True when the todo is only visible to the creator (not shared with the team). */
export const isTodoPrivate = (todo) => {
	if (hasNonCreatorAssignees(todo)) {
		return false;
	}
	if (typeof todo?.private === 'boolean') {
		return todo.private;
	}
	return true;
};

export const canTodoBePrivate = (todo, assigneeIds = todo?.assignedTo) =>
	!hasNonCreatorAssignees(todo, assigneeIds);

export const normalizeTaskItem = (task, extras = {}) => {
	const reviewer = task.reviewer ? mapReviewer(task.reviewer) : null;
	return {
		id: String(task._id),
		title: task.title || task.description || 'Untitled',
		status: normalizeWorkItemStatus(task.status),
		priority: task.priority || null,
		dueDate: task.dueDate || task.toCompleteBy || null,
		assignees: normalizeAssignees(task.assignedTo).map((user) => ({
			id: String(user._id || user),
			name: getAssigneeName(user),
			avatar: user.avatar || user.profilePicture || null,
		})),
		reviewer,
		reviewerId: reviewer?.id || null,
		projectTitle: extras.projectTitle || task.projectTitle || null,
		projectId: extras.projectId || task.projectId || null,
		taskId: extras.taskId || null,
		itemType: extras.itemType || 'task',
		statusOptions: WORK_ITEM_STATUS_OPTIONS,
		raw: task,
	};
};

export const normalizeTodoItem = (todo) => {
	const assignees = normalizeAssignees(todo.assignedTo);
	const reviewer = todo.reviewer ? mapReviewer(todo.reviewer) : null;
	const creator = (todo.users || [])[0];
	const creatorName = creator ? getAssigneeName(creator) : null;

	return {
		id: String(todo._id),
		title: todo.description || 'Untitled todo',
		status: normalizeWorkItemStatus(todo.status),
		priority: todo.priority || null,
		dueDate: todo.toCompleteBy || null,
		assignees: assignees.map((user) => ({
			id: String(user._id || user),
			name: getAssigneeName(user),
			avatar: user.avatar || user.profileImage || null,
		})),
		reviewer,
		reviewerId: reviewer?.id || null,
		projectTitle: null,
		projectId: null,
		taskId: null,
		itemType: 'todo',
		statusOptions: WORK_ITEM_STATUS_OPTIONS,
		raw: todo,
		isPrivate: isTodoPrivate(todo),
		creatorId: String(creator?._id || creator || ''),
		creatorName,
	};
};

export const normalizeProjectTaskItems = (projects) =>
	projects.flatMap((project) =>
		(project.tasks || []).map((task) =>
			normalizeTaskItem(task, {
				projectTitle: project.generalInfo?.title,
				projectId: String(project._id),
				itemType: 'task',
			}),
		),
	);

export const groupItemsByStatus = (items) => {
	const grouped = {};
	STATUS_GROUPS.forEach((group) => {
		grouped[group.key] = [];
	});
	items.forEach((item) => {
		const key = getStatusGroup(item.status);
		grouped[key].push(item);
	});
	return grouped;
};

export const toDateKey = (date) => {
	const d = new Date(date);
	const pad = (n) => String(n).padStart(2, '0');
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const buildCalendarGrid = (year, month) => {
	const firstDow = new Date(year, month, 1).getDay();
	const daysInMonth = new Date(year, month + 1, 0).getDate();
	const daysInPrev = new Date(year, month, 0).getDate();

	const cells = [];
	for (let i = firstDow - 1; i >= 0; i--) {
		cells.push({ day: daysInPrev - i, current: false });
	}
	for (let d = 1; d <= daysInMonth; d++) {
		cells.push({ day: d, current: true, date: new Date(year, month, d) });
	}

	let next = 1;
	while (cells.length % 7 !== 0) {
		cells.push({ day: next++, current: false });
	}

	return cells;
};

export const CALENDAR_DAY_LABELS = [
	'Sunday',
	'Monday',
	'Tuesday',
	'Wednesday',
	'Thursday',
	'Friday',
	'Saturday',
];

export const CALENDAR_MONTHS = [
	'January',
	'February',
	'March',
	'April',
	'May',
	'June',
	'July',
	'August',
	'September',
	'October',
	'November',
	'December',
];

export const WORKSPACE_TASK_PRIORITY_OPTIONS = [
	{ value: 'low', label: 'Low' },
	{ value: 'medium', label: 'Medium' },
	{ value: 'high', label: 'High' },
	{ value: 'urgent', label: 'Urgent' },
];

export const WORKSPACE_TODO_PRIORITY_OPTIONS = [
	{ value: 'low', label: 'Low' },
	{ value: 'medium', label: 'Medium' },
	{ value: 'high', label: 'High' },
];

export const getWorkspacePriorityOptions = (item) =>
	item?.itemType === 'todo'
		? WORKSPACE_TODO_PRIORITY_OPTIONS
		: WORKSPACE_TASK_PRIORITY_OPTIONS;

export const getWorkspaceItemTypeMeta = (itemType) => {
	const map = {
		todo: { label: 'Todo', colorScheme: 'purple' },
		task: { label: 'Task', colorScheme: 'blue' },
		subtask: { label: 'Subtask', colorScheme: 'teal' },
	};
	return map[itemType] || { label: 'Item', colorScheme: 'gray' };
};

export const getCompletedItemStyles = (status, textColor, subtitleColor) => {
	if (!isTodoCompletedStatus(status)) {
		return { color: textColor, textDecoration: 'none' };
	}
	return {
		color: subtitleColor,
		textDecoration: 'line-through',
	};
};

export const isTodoCreator = (todo, userId) => {
	const creatorId = getTodoCreatorId(todo);
	return creatorId ? creatorId === String(userId) : false;
};

export const canUserUpdateTodoStatus = (todo, userId) => {
	if (isTodoCreator(todo, userId)) return true;
	return isUserInAssignees(todo.assignedTo, userId);
};

export const buildTodoAssigneesOnAdd = (todo, employeeId) => {
	const currentIds = normalizeAssignees(todo.assignedTo).map((user) =>
		String(user._id || user),
	);
	const creatorId = getTodoCreatorId(todo);
	const nextIds = [...currentIds, String(employeeId)];
	if (creatorId) {
		nextIds.push(creatorId);
	}
	return Array.from(new Set(nextIds));
};

export const isUserInAssignees = (assignedTo, userId) =>
	normalizeAssignees(assignedTo).some(
		(user) => String(user._id || user) === String(userId),
	);

export const isProjectManagerForUser = (project, userId) =>
	(project?.team || []).some(
		(member) =>
			String(member.assignedTeamEmployee?._id || member.assignedTeamEmployee) ===
				String(userId) && member.isProjectManager === true,
	);

/** PM-only actions on a project (milestones, invoices, documents, risks). */
export const canManageProjectAsManager = (project, userId, isCompanyAdmin = false) =>
	isCompanyAdmin || isProjectManagerForUser(project, userId);

/** Project task edits require project update permission AND PM or company admin. */
export const canManageProjectTasks = (
	project,
	userId,
	canUpdateProjects = false,
	isCompanyAdmin = false,
) =>
	canUpdateProjects && (isCompanyAdmin || isProjectManagerForUser(project, userId));

export const isPublicTodoVisibleToUser = (todo, userId) => {
	if (!isTodoPrivate(todo)) {
		return true;
	}

	if (isUserInAssignees(todo.assignedTo, userId)) {
		return true;
	}

	return (todo.users || []).some((user) => String(user._id || user) === String(userId));
};

export const collectAssignedWorkItemsFromProject = (project, userId) => {
	const projectTitle = project.generalInfo?.title;
	const projectId = project._id;
	const items = [];

	(project.tasks || []).forEach((task) => {
		if (isUserInAssignees(task.assignedTo, userId)) {
			items.push({ ...task, projectTitle, projectId });
		}

		(task.subtasks || []).forEach((subtask) => {
			if (isUserInAssignees(subtask.assignedTo, userId)) {
				items.push({
					...subtask,
					projectTitle,
					projectId,
					taskId: task._id,
					taskTitle: task.title,
				});
			}
		});
	});

	return items;
};

export const collectAllWorkItemsFromProject = (project) => {
	const projectTitle = project.generalInfo?.title;
	const projectId = project._id;
	const items = [];

	(project.tasks || []).forEach((task) => {
		items.push({ ...task, projectTitle, projectId });
		(task.subtasks || []).forEach((subtask) => {
			items.push({
				...subtask,
				projectTitle,
				projectId,
				taskId: task._id,
				taskTitle: task.title,
			});
		});
	});

	return items;
};

export const collectVisibleWorkItems = ({
	projects = [],
	userId,
	isCompanyAdmin = false,
	showAllCompanyTasks = false,
	expandedPmProjectIds = new Set(),
	autoExpandPmProjects = false,
}) => {
	if (isCompanyAdmin && showAllCompanyTasks) {
		return projects.flatMap((project) => collectAllWorkItemsFromProject(project));
	}

	return projects.flatMap((project) => {
		const projectId = String(project._id);
		const isPmOnProject = isProjectManagerForUser(project, userId);
		const showAllInProject =
			(autoExpandPmProjects && isPmOnProject) ||
			(expandedPmProjectIds.has(projectId) && isPmOnProject);

		if (showAllInProject) {
			return collectAllWorkItemsFromProject(project);
		}

		return collectAssignedWorkItemsFromProject(project, userId);
	});
};

export const collectPendingReviewItems = ({
	todos = [],
	projects = [],
	userId,
}) => {
	const matchesReviewer = (reviewer) =>
		reviewer && String(reviewer._id || reviewer) === String(userId);

	const todoItems = todos
		.filter(
			(todo) =>
				isReadyToReviewStatus(todo.status) && matchesReviewer(todo.reviewer),
		)
		.map(normalizeTodoItem);

	const projectItems = projects.flatMap((project) =>
		(project.tasks || []).flatMap((task) => {
			const extras = {
				projectTitle: project.generalInfo?.title,
				projectId: String(project._id),
			};
			const items = [];

			if (isReadyToReviewStatus(task.status) && matchesReviewer(task.reviewer)) {
				items.push(normalizeTaskItem(task, { ...extras, itemType: 'task' }));
			}

			(task.subtasks || []).forEach((subtask) => {
				if (
					isReadyToReviewStatus(subtask.status) &&
					matchesReviewer(subtask.reviewer)
				) {
					items.push(
						normalizeTaskItem(subtask, {
							...extras,
							taskId: String(task._id),
							itemType: 'subtask',
						}),
					);
				}
			});

			return items;
		}),
	);

	return [...todoItems, ...projectItems];
};
